import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import Breadcrumb from "../ui/Breadcrumb/Breadcrumb";

export default function CmsPage({
  slug,
  fallbackTitle = "",
  fallbackContent = "",
}) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiSlug = useMemo(() => {
    const value = String(slug || "").trim();

    const slugMap = {
      about: "about",
      services: "services",
      contact: "contact",
      "privacy-policy": "privacy_policy",
      terms: "terms",
      "shipping-policy": "shipping_policy",
      "refund-policy": "refund_policy",
    };

    return slugMap[value] || value.replace(/-/g, "_");
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, lang]);

  useEffect(() => {
    let mounted = true;

    async function tryLoadFromLaravel() {
      return await api.apiGet(
        `pages?slug=${encodeURIComponent(apiSlug)}&lang=${encodeURIComponent(lang)}`,
      );
    }

    async function tryLoadFromLegacy() {
      return await api.apiGet(
        `pages/get.php?slug=${encodeURIComponent(apiSlug)}&lang=${encodeURIComponent(lang)}`,
      );
    }

    function normalizePageData(data) {
      const pageData =
        data?.data ||
        data?.page ||
        data?.item ||
        (data?.status === "success" ? data?.data : null);

      if (!pageData) return null;

      const title =
        pageData.title ||
        (isAr ? pageData.title_ar : pageData.title_en) ||
        fallbackTitle ||
        apiSlug.replace(/[-_]/g, " ");

      const content =
        pageData.content ||
        (isAr ? pageData.content_ar : pageData.content_en) ||
        fallbackContent ||
        "";

      const updatedAt =
        pageData.updated_at_human ||
        pageData.updated_at ||
        pageData.created_at ||
        null;

      return { title, content, updatedAt };
    }

    async function load() {
      if (!apiSlug) {
        if (!mounted) return;
        setError("Missing slug parameter");
        setLoading(false);
        return;
      }

      try {
        if (!mounted) return;
        setLoading(true);
        setError(null);

        try {
          const laravelData = await tryLoadFromLaravel();
          const normalizedLaravel = normalizePageData(laravelData);

          if (normalizedLaravel) {
            if (!mounted) return;
            setPage(normalizedLaravel);
            return;
          }
        } catch (e) {
          // ignore and fallback to legacy
        }

        try {
          const legacyData = await tryLoadFromLegacy();
          const normalizedLegacy = normalizePageData(legacyData);

          if (normalizedLegacy) {
            if (!mounted) return;
            setPage(normalizedLegacy);
            return;
          }

          if (!mounted) return;
          setError(legacyData?.message || "Error loading page content.");
          setPage({
            title: fallbackTitle || apiSlug.replace(/[-_]/g, " "),
            content: fallbackContent || "",
            updatedAt: null,
          });
        } catch (e) {
          if (!mounted) return;
          setError(e?.message || "Error loading page content.");
          setPage({
            title: fallbackTitle || apiSlug.replace(/[-_]/g, " "),
            content: fallbackContent || "",
            updatedAt: null,
          });
        }
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [apiSlug, lang, isAr, fallbackTitle, fallbackContent]);

  const pageTitle =
    page?.title ||
    fallbackTitle ||
    apiSlug?.replace?.(/[-_]/g, " ") ||
    (isAr ? "الصفحة" : "Page");

  const breadcrumbItems = [
    { label: pageTitle }
  ];

  return (
    <div className={`cms-page-wrapper ${isAr ? "cms-rtl" : "cms-ltr"}`}>
      <div className="container my-4">
        <Breadcrumb items={breadcrumbItems} />

        <div className="cms-card card shadow-sm border-0">
          <div className="card-body p-4 p-md-5">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
              <h1 className="cms-title mb-0">{pageTitle}</h1>

              {page?.updatedAt && (
                <div className="cms-updated text-muted small">
                  <i className="bi bi-clock-history me-1" />
                  {isAr ? "آخر تحديث: " : "Last updated: "}
                  {page.updatedAt}
                </div>
              )}
            </div>

            <hr className="mb-4 mt-2" />

            {loading && (
              <div className="cms-loading">
                <div className="cms-skeleton mb-2" />
                <div className="cms-skeleton w-75 mb-2" />
                <div className="cms-skeleton w-50 mb-2" />
              </div>
            )}

            {!loading && (
              <>
                {error && !page?.content && !fallbackContent && (
                  <div className="alert alert-warning" role="alert">
                    {isAr
                      ? "حدث خطأ أثناء تحميل المحتوى. يرجى المحاولة لاحقاً."
                      : "An error occurred while loading this page. Please try again later."}
                  </div>
                )}

                {page?.content || fallbackContent ? (
                  <div
                    className="cms-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(page?.content || fallbackContent),
                    }}
                  />
                ) : (
                  <p className="text-muted">
                    {isAr
                      ? "لا يوجد محتوى متاح لهذه الصفحة حالياً."
                      : "No content available for this page yet."}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}